import React, { createContext, useContext, useState, useEffect } from 'react'
import config from '../config/api'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token')
      if (token) {
        const response = await fetch(`${config.TASKS_API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const userData = await response.json()
          console.log('Auth check - user data from server:', userData)
          setUser(userData)
        } else {
          localStorage.removeItem('token')
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      localStorage.removeItem('token')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password, turnstileToken = null) => {
    try {
      const response = await fetch(`${config.TASKS_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, turnstile_token: turnstileToken })
      })

      if (response.ok) {
        const data = await response.json()
        const { access_token } = data

        localStorage.setItem('token', access_token)

        // Fetch user info after login
        const userResponse = await fetch(`${config.TASKS_API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json'
          }
        })

        if (userResponse.ok) {
          const userData = await userResponse.json()
          setUser(userData)
        }

        return { success: true }
      } else {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Login failed')
      }
    } catch (error) {
      throw new Error(error.message || 'Login failed')
    }
  }

  // Login with existing token (for Google OAuth callback)
  const loginWithToken = async (token) => {
    try {
      localStorage.setItem('token', token)

      const response = await fetch(`${config.TASKS_API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const userData = await response.json()
        console.log('Google login - user data:', userData)
        setUser(userData)
        return { success: true, user: userData }
      } else {
        localStorage.removeItem('token')
        throw new Error('Failed to fetch user data')
      }
    } catch (error) {
      localStorage.removeItem('token')
      throw new Error(error.message || 'Login with token failed')
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token')
      if (token) {
        const response = await fetch(`${config.TASKS_API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const userData = await response.json()
          console.log('Refresh user - user data from server:', userData)
          setUser(userData)
          return userData
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error)
    }
    return null
  }

  const value = {
    user,
    login,
    loginWithToken,
    logout,
    loading,
    setUser,
    refreshUser,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
